import { formatDistanceToNow } from 'date-fns';
import { Globe, Zap } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import { FlatList, Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { FadeIn } from '../../../src/components/animations';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
} from '../../../src/components/icons/BrandIcons';
import { HStack, Text, VStack } from '../../../src/components/primitives';
import { Box } from '../../../src/components/primitives/Box';
import { EmptyState } from '../../../src/components/primitives/EmptyState';
import { WorkflowForm } from '../../../src/components/workflow';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useTranslation } from '../../../src/contexts/TranslationContext';
import {
  type Execution,
  ExecutionHistory,
  mapExecutionToRow,
  WorkflowExecutionDetailSheet,
  WorkflowListItem,
  type Workflow as WorkflowType,
  WorkflowVisualizer,
} from '../../../src/features/workflows';
import { useWorkflowExecutions } from '../../../src/hooks/useWorkflowExecutions';
import {
  useToggleWorkflowPause,
  useWorkflowSettings,
  type WorkflowType as WorkflowTypeEnum,
} from '../../../src/hooks/useWorkflowSettings';
import { DATE_LOCALES } from '../../../src/lib/dateLocales';
import type { SocialMedia, WorkflowExecutionRecord } from '../../../src/lib/workflowsApi';
import { colors } from '../../../src/theme/colors';
import { tokens } from '../../../src/theme/tokens';

const WORKFLOW_ID_TO_TYPE: Record<string, WorkflowTypeEnum> = {
  '1': 'linkedin_enrichment',
  '5': 'instagram',
  '4': 'web_search_contact',
};

const COL_BREAKPOINT_3 = 1280;
const COL_BREAKPOINT_2 = 780;
const EXECUTIONS_PAGE_SIZE = 10;

type WorkflowOrPlaceholder = WorkflowType | { id: string; __placeholder: true };

function padForGrid(items: WorkflowType[], numColumns: number): WorkflowOrPlaceholder[] {
  if (numColumns <= 1) return items;
  const remainder = items.length % numColumns;
  if (remainder === 0) return items;
  const fillers: WorkflowOrPlaceholder[] = Array.from(
    { length: numColumns - remainder },
    (_, i) => ({ id: `__wf_placeholder_${i}`, __placeholder: true }),
  );
  return [...items, ...fillers];
}

function WorkflowListSeparator() {
  return <View style={{ height: 8 }} />;
}

export default function WorkflowsScreen() {
  const { currentOrganization } = useAuth();
  const { t, language } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [formSocialMedia, setFormSocialMedia] = useState<SocialMedia>('LinkedIn');
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType | null>(null);
  const [executionsPage, setExecutionsPage] = useState(0);
  const [selectedExecution, setSelectedExecution] = useState<{
    record: WorkflowExecutionRecord;
    workflowName: string;
  } | null>(null);
  const { width: viewportWidth } = useWindowDimensions();

  const numColumns = useMemo(() => {
    if (viewportWidth >= COL_BREAKPOINT_3) return 3;
    if (viewportWidth >= COL_BREAKPOINT_2) return 2;
    return 1;
  }, [viewportWidth]);

  const { isWorkflowPaused } = useWorkflowSettings(currentOrganization?.id);
  const { mutate: togglePause } = useToggleWorkflowPause();

  const {
    data: executionsData,
    isLoading: executionsLoading,
    isFetching: executionsFetching,
  } = useWorkflowExecutions(
    currentOrganization?.id,
    EXECUTIONS_PAGE_SIZE,
    executionsPage * EXECUTIONS_PAGE_SIZE,
  );

  const linkedinStats = executionsData?.stats?.totalByType?.linkedin_enrichment;
  const instagramStats = executionsData?.stats?.totalByType?.instagram_enrichment_direct;
  const webSearchStats = executionsData?.stats?.totalByType?.web_search_contact;

  const dateLocale = DATE_LOCALES[language];

  const linkedinLastRun = linkedinStats?.lastRun
    ? formatDistanceToNow(new Date(linkedinStats.lastRun), {
        addSuffix: true,
        ...(dateLocale && { locale: dateLocale }),
      })
    : null;

  const instagramLastRun = instagramStats?.lastRun
    ? formatDistanceToNow(new Date(instagramStats.lastRun), {
        addSuffix: true,
        ...(dateLocale && { locale: dateLocale }),
      })
    : null;

  const webSearchLastRun = webSearchStats?.lastRun
    ? formatDistanceToNow(new Date(webSearchStats.lastRun), {
        addSuffix: true,
        ...(dateLocale && { locale: dateLocale }),
      })
    : null;

  const workflows: WorkflowType[] = useMemo(
    () => [
      {
        id: '1',
        name: t('workflow.mock.linkedinEnrichment.name'),
        description: t('workflow.mock.linkedinEnrichment.description'),
        icon: <LinkedinIcon size={22} color={colors.primary.neon} />,
        status: isWorkflowPaused('linkedin_enrichment') ? ('paused' as const) : ('active' as const),
        lastRun: linkedinLastRun,
        executionCount: linkedinStats?.count ?? 0,
      },
      {
        id: '5',
        name: t('workflow.mock.instagramEnrichment.name'),
        description: t('workflow.mock.instagramEnrichment.description'),
        icon: <InstagramIcon size={22} color={colors.accent.purple[400]} />,
        status: isWorkflowPaused('instagram') ? ('paused' as const) : ('active' as const),
        lastRun: instagramLastRun,
        executionCount: instagramStats?.count ?? 0,
      },
      {
        id: '2',
        name: t('workflow.mock.facebookScraper.name'),
        description: t('workflow.mock.facebookScraper.description'),
        icon: <FacebookIcon size={22} color={colors.status.info.main} />,
        status: 'paused' as const,
        lastRun: null,
        executionCount: 0,
        disabled: true,
      },
      {
        id: '3',
        name: t('workflow.mock.leadScoring.name'),
        description: t('workflow.mock.leadScoring.description'),
        icon: <Zap size={22} color={colors.status.warning.main} />,
        status: 'paused' as const,
        lastRun: null,
        executionCount: 0,
        disabled: true,
      },
      {
        id: '4',
        name: t('workflow.mock.webScraper.name'),
        description: t('workflow.mock.webScraper.description'),
        icon: <Globe size={22} color={colors.accent.purple[400]} />,
        status: isWorkflowPaused('web_search_contact') ? ('paused' as const) : ('active' as const),
        lastRun: webSearchLastRun,
        executionCount: webSearchStats?.count ?? 0,
        statsOnly: true,
      },
    ],
    [
      t,
      isWorkflowPaused,
      linkedinLastRun,
      linkedinStats?.count,
      instagramLastRun,
      instagramStats?.count,
      webSearchLastRun,
      webSearchStats?.count,
    ],
  );

  const executions: Execution[] = useMemo(() => {
    if (!executionsData?.executions) return [];
    return executionsData.executions.map((e) => mapExecutionToRow(e, t, language));
  }, [executionsData?.executions, t, language]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional - only update on workflows change
  useEffect(() => {
    if (workflows.length === 0) return;
    if (selectedWorkflow) {
      const updated = workflows.find((w) => w.id === selectedWorkflow.id);
      if (updated) setSelectedWorkflow(updated);
    } else {
      setSelectedWorkflow(workflows[0]);
    }
  }, [workflows]);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    setExecutionsPage(0);
    setSelectedExecution(null);
  }, [currentOrganization?.id]);

  const activeCount = workflows.filter((w) => !w.disabled && w.status === 'active').length;

  const handleRun = (workflow: WorkflowType) => {
    if (workflow.disabled || workflow.statsOnly) return;
    setFormSocialMedia(workflow.id === '5' ? 'Instagram' : 'LinkedIn');
    setSelectedWorkflow(workflow);
    setShowForm(true);
  };

  const handleTogglePause = (workflow: WorkflowType) => {
    const workflowTypeKey = WORKFLOW_ID_TO_TYPE[workflow.id];
    if (!workflowTypeKey) return;
    togglePause({ workflowType: workflowTypeKey, paused: workflow.status !== 'paused' });
  };

  const renderItem = ({ item }: { item: WorkflowOrPlaceholder }) => {
    if ('__placeholder' in item) {
      return <View style={layoutStyles.gridItem} />;
    }
    const isBacked = !!WORKFLOW_ID_TO_TYPE[item.id];
    const node = (
      <WorkflowListItem
        workflow={item}
        numColumns={numColumns}
        onPress={() => setSelectedWorkflow(item)}
        onRun={item.disabled || item.statsOnly ? undefined : () => handleRun(item)}
        onTogglePause={item.disabled || !isBacked ? undefined : () => handleTogglePause(item)}
        onEdit={item.disabled || item.statsOnly ? undefined : () => handleRun(item)}
      />
    );
    return numColumns > 1 ? <View style={layoutStyles.gridItem}>{node}</View> : node;
  };

  return (
    <View testID="workflows_screen" style={layoutStyles.root}>
      <View style={layoutStyles.content}>
        <FadeIn delay={0} translateY={12} style={layoutStyles.sectionSpacing}>
          <HStack justify="between" align="center" wrap style={layoutStyles.toolbarRow}>
            <VStack spacing="xs" style={layoutStyles.toolbarTitle}>
              <Text variant="h4" color={colors.text.primary} style={layoutStyles.heading}>
                {t('workflow.page.title')}
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {activeCount} {t('workflow.card.status.active').toLowerCase()} · {workflows.length}{' '}
                {workflows.length === 1 ? 'workflow' : 'workflows'}
              </Text>
            </VStack>
          </HStack>
        </FadeIn>

        {workflows.length > 0 ? (
          <FlatList<WorkflowOrPlaceholder>
            testID="workflows_list"
            key={`workflows-cols-${numColumns}`}
            data={padForGrid(workflows, numColumns)}
            keyExtractor={(item) => item.id}
            numColumns={numColumns}
            columnWrapperStyle={numColumns > 1 ? layoutStyles.columnWrapper : undefined}
            renderItem={renderItem}
            ItemSeparatorComponent={numColumns === 1 ? WorkflowListSeparator : undefined}
            contentContainerStyle={layoutStyles.listContent}
            ListFooterComponent={
              <VStack spacing="xl" style={{ marginTop: tokens.spacing.xl }}>
                {selectedWorkflow && (
                  <WorkflowVisualizer
                    name={selectedWorkflow.name}
                    description={selectedWorkflow.description}
                    delay={400}
                  />
                )}

                <ExecutionHistory
                  executions={executions}
                  loading={executionsLoading}
                  fetching={executionsFetching}
                  delay={500}
                  pagination={
                    executionsData?.pagination
                      ? {
                          page: executionsPage + 1,
                          pageCount: Math.max(
                            1,
                            Math.ceil(executionsData.pagination.total / EXECUTIONS_PAGE_SIZE),
                          ),
                          total: executionsData.pagination.total,
                        }
                      : undefined
                  }
                  onPreviousPage={() => setExecutionsPage((page) => Math.max(0, page - 1))}
                  onNextPage={() => setExecutionsPage((page) => page + 1)}
                  onExecutionPress={(execution) => {
                    if (!execution.source) return;
                    setSelectedExecution({
                      record: execution.source,
                      workflowName: execution.workflowName,
                    });
                  }}
                />
              </VStack>
            }
            showsVerticalScrollIndicator={false}
          />
        ) : (
          <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
            <EmptyState
              icon={<Zap size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
              title={t('workflow.page.emptyTitle')}
              description={t('workflow.page.emptyDescription')}
              action={{
                label: t('workflow.page.emptyAction'),
                onPress: () => setShowForm(true),
              }}
            />
          </Box>
        )}
      </View>

      <WorkflowExecutionDetailSheet
        execution={selectedExecution?.record ?? null}
        workflowName={selectedExecution?.workflowName ?? ''}
        visible={selectedExecution !== null}
        onClose={() => setSelectedExecution(null)}
      />

      <WorkflowForm
        visible={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={() => {
          setShowForm(false);
        }}
        socialMedia={formSocialMedia}
      />
    </View>
  );
}

const layoutStyles = StyleSheet.create({
  root: {
    flex: 1,
    position: 'relative',
    ...(Platform.OS === 'web'
      ? { overflow: 'hidden' as const, width: '100%', maxWidth: '100%' }
      : {}),
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    // Match the dashboard tab's content inset so the tabs align visually.
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    ...(Platform.OS === 'web' ? { minWidth: 0, width: '100%', maxWidth: '100%' } : {}),
  },
  toolbarRow: {
    rowGap: tokens.spacing.sm,
    width: '100%',
  },
  toolbarTitle: {
    flexShrink: 1,
    minWidth: 0,
  },
  sectionSpacing: {
    marginBottom: tokens.spacing.lg,
  },
  heading: {
    fontWeight: tokens.fontWeight.bold,
  },
  listContent: {
    paddingVertical: 4,
    paddingBottom: tokens.spacing['3xl'],
  },
  columnWrapper: {
    marginBottom: tokens.spacing.md,
    columnGap: tokens.spacing.md,
  },
  gridItem: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
});
